import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Loader2, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { mfaEnrollStart, mfaEnrollConfirm, mfaDisable } from "@/api";

// Configuración de 2FA (TOTP) para roles de alto privilegio. Se usa tanto en el
// backoffice de Valoriza/Ayuntamiento como en el dashboard del Técnico Ayuntamiento.
export default function MfaSettings({ enabled, onChanged }) {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const start = async () => {
    setOpen(true);
    try {
      const res = await mfaEnrollStart();
      setQr(res.qr_data_url);
    } catch {
      toast.error("No se pudo iniciar el enrolamiento 2FA");
    }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      await mfaEnrollConfirm(code);
      toast.success("2FA activado correctamente");
      setOpen(false);
      setQr(null);
      setCode("");
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Código inválido"));
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    try {
      await mfaDisable();
      toast.success("2FA desactivado");
      onChanged?.();
    } catch {
      toast.error("No se pudo desactivar el 2FA");
    }
  };

  if (enabled) {
    return (
      <Button variant="outline" size="sm" onClick={disable} className="border-amber-400 text-amber-600" data-testid="mfa-disable-btn">
        <ShieldOff className="w-4 h-4 mr-2" /> Desactivar 2FA
      </Button>
    );
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={start} className="border-municipal text-municipal" data-testid="mfa-setup-btn">
        <KeyRound className="w-4 h-4 mr-2" /> Configurar 2FA
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="mfa-enroll-dialog">
          <DialogHeader><DialogTitle className="text-municipal font-heading">Activar verificación en dos pasos</DialogTitle></DialogHeader>
          {qr ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-500">Escanea este código con Google Authenticator o Authy</p>
              <img src={qr} alt="Código QR de activación 2FA" className="mx-auto rounded-lg border border-gray-200" data-testid="mfa-qr-image" />
              <Input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="text-center text-lg tracking-widest"
                data-testid="mfa-enroll-code-input"
              />
              <Button onClick={confirm} disabled={loading || code.length !== 6} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="mfa-enroll-confirm-btn">
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Confirmar y activar
              </Button>
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-municipal" /></div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
